import React, { useCallback } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { captureException } from '@sentry/nextjs';
import Head from 'next/head';

import VdpOrNotFound from 'pages/listings/[vin]';
import Error from 'pages/_error';
import { wrapper } from 'pages/_app';

import NewSrp from 'components/Srp/NewSrp';
import Layout, { LayoutProps } from 'components/Layout';
import StructuredDataComponent from 'components/StructuredData';
import SeoSrpHead from 'components/SeoSrp/SeoSrpHead';
import Banner from 'components/SeoSrp/Banner';
import { v4 as uuidv4 } from 'uuid';

import Geocode from 'lib/location/Locations/Base';

import {
  fetchSearchUrl,
  searchVehicles,
  VehicleV2,
  SearchParamsV2,
  VehicleOverviewInterface,
  fetchVehicleOverview,
  fetchDealerReviews,
  fetchModelReviews,
  fetchArticlePreviewsOfParentPage,
  ArticlePreview,
  fetchDrivingExperiencesOfCity,
  DrivingExperiences,
  fetchTopDealers,
  TopDealers,
  withTiming,
  FacetCounts,
} from 'lib/AutolistAPI';
import i18next from 'lib/i18next';
import useBuildVdpPageProps from 'lib/hooks/useBuildVdpPageProps';
import useSeoSrpModel from 'lib/hooks/useSeoSrpModel';
import useGeoLocation, { useGeolocationData } from 'lib/hooks/useGeoLocation';
import { useShowVdp } from 'lib/hooks/useShowVdp';
import { SourcePages } from 'lib/EventSchema';
import MetroCodes from 'lib/config/metroCodeMapping.json';
import {
  SearchFilterUrlParams,
  transformSrpFilterApiParamsToState,
} from 'lib/srpFilterTransformers';
import renderGeoIP from 'components/tags/GeoIP';

import { setSrpFilters, srpFilterInitialValues } from 'redux-implementations/srpFilters';

import {
  ModelReviewsInterface,
  SearchUrlAttributes,
  DealerReviewsInterface,
} from 'types/Apis/SearchURL';
import SrpEventContext from 'contexts/SrpEventContext';
import { SrpFilterName } from 'types/srpFilters';
import AdvancedBanner from 'components/SeoSrp/AdvancedBanner';
import { AdGlobalRefreshProvider } from 'contexts/AdGlobalRefreshContext';
import { IncomingMessage, ServerResponse } from 'http';
import useHashLocation from 'lib/hooks/useHashLocation';
import { getIpMaxMindValue, setIpMaxMindValue } from 'lib/cache/Redis';
import getIpAddress from 'lib/getIpAddress';
import { geolocateRequest } from 'lib/location/Geolocate';

type MetroCodesType = Record<string, { latitude: number; longitude: number }>;
export interface SeoSrpPageProps {
  statusCode: number;
  vehicles?: VehicleV2[];
  totalCount?: number;
  searchUrlAttributes?: SearchUrlAttributes;
  vehicleOverview?: VehicleOverviewInterface;
  ownerReviews?: ModelReviewsInterface;
  latitude?: number;
  longitude?: number;
  dealerReviews?: DealerReviewsInterface;
  articlePreviews?: ArticlePreview[];
  drivingTipsAndExperiences?: DrivingExperiences;
  contentGroup?: string;
  topDealers?: TopDealers;
  facetCounts?: FacetCounts;
}

function toRating(rating: number): number {
  return parseFloat(parseFloat(String(rating)).toFixed(1));
}

export default SeoSrpPageWrapper;
export function SeoSrpPageWrapper(props: SeoSrpPageProps): JSX.Element {
  if (props.statusCode === 500) {
    return <Error statusCode={500} />;
  }

  return <SeoSrpPageInner {...props} />;
}

export function SeoSrpPageInner(props: SeoSrpPageProps): JSX.Element {
  const initialParams = useSeoSrpModel(props);
  const { t } = useTranslation();
  const {
    title,
    description,
    vehicles,
    isLoading,
    statusCode,
    searchVehicles: onSearch,
    totalCount,
    bannerTitle,
    adInfo,
    facetCounts,
  } = initialParams;

  const {
    values: { vin },
  } = useHashLocation();

  const clientGeolocation = useGeolocationData();
  // skip browser geolocate when user goes to vdp and no geolocation set yet
  const skipClientGeolocate = vin !== undefined && !clientGeolocation?.toJSON();
  const { setGeolocation } = useGeoLocation(skipClientGeolocate);
  // use server geolocation when going directly to vdp
  if (skipClientGeolocate) {
    const serverLocation = new Geocode({
      city: '',
      stateAbbreviation: '',
      longitude: props.longitude || 0,
      latitude: props.latitude || 0,
      zip: '',
    });
    setGeolocation(serverLocation);
  }

  const vdpProps = useBuildVdpPageProps();
  const [showVdp] = useShowVdp(vdpProps);

  function getVdpLayoutProps(): LayoutProps {
    return {
      title: vdpProps.vehicle
        ? `${vdpProps.vehicle.year} ${vdpProps.vehicle.make} ${vdpProps.vehicle.model}`
        : t(`main.meta.pageTitle`),
      description: '',
      includeAds: true,
      indexed: false,
      footer: <div />,
      showHeader: false,
      thumbnail: vdpProps.vehicle?.photo_urls[0],
    };
  }

  const { asPath } = useRouter();

  function getSrpLayoutProps(): LayoutProps {
    return {
      title: title ?? 'SEO SRP',
      description: description ?? '',
      indexed: !asPath.includes('noindex'),
    };
  }

  function scrollToReviews(): void {
    const element = document.getElementById('reviews-container');
    element?.scrollIntoView({ behavior: 'smooth' });
  }

  const layoutProps = showVdp ? getVdpLayoutProps() : getSrpLayoutProps();

  const buildAdvancedBannerProps = useCallback(
    (ownerReviews?: ModelReviewsInterface) => {
      if (ownerReviews && ownerReviews.ownerReviews.length > 0) {
        const {
          ownerReviewSummary: { reviewCount, overallRating, breakdownByCategoryAverages },
        } = ownerReviews;

        return {
          reviewCount,
          overallRating: toRating(overallRating),
          ratingAverages: {
            valueRating: toRating(breakdownByCategoryAverages.valueRating),
            performanceRating: toRating(breakdownByCategoryAverages.performanceRating),
            styleRating: toRating(breakdownByCategoryAverages.styleRating),
            comfortRating: toRating(breakdownByCategoryAverages.comfortRating),
            fuelEconomyRating: toRating(breakdownByCategoryAverages.fuelEconomyRating),
            maintenanceRating: toRating(breakdownByCategoryAverages.maintenanceRating),
          },
          title: bannerTitle,
        };
      }

      return undefined;
    },
    [bannerTitle]
  );
  const advancedBannerProps = buildAdvancedBannerProps(props.ownerReviews);

  if (props.statusCode !== 200 || (statusCode && statusCode !== 200)) {
    return <Error statusCode={props.statusCode} />;
  }

  const alternateAndroidUrlPath = new URLSearchParams(
    props.searchUrlAttributes?.search_params as unknown as Record<string, string>
  ).toString();

  const highPrice = Math.max.apply(
    null,
    vehicles.map((vehicle) => vehicle.price)
  );

  const lowPrice = Math.min.apply(
    null,
    vehicles.map((vehicle) => vehicle.price)
  );

  return (
    <div>
      <SeoSrpHead title={title ?? 'SEO SRP'} description={description ?? ''} />
      <div id="srp-wrapper">
        <AdGlobalRefreshProvider>
          <Layout
            {...layoutProps}
            alternateAndroidUrl={`android-app://com.autolist.autolist/autolist/search?${alternateAndroidUrlPath}`}
          >
            <Head>{renderGeoIP()}</Head>
            {showVdp ? (
              <VdpOrNotFound {...vdpProps} />
            ) : (
              <>
                {advancedBannerProps ? (
                  <AdvancedBanner {...advancedBannerProps} onReviewsClick={scrollToReviews} />
                ) : (
                  <Banner bannerTitle={bannerTitle} />
                )}
                <SrpEventContext.Provider
                  value={{ sourcePage: SourcePages.SeoSrp, sourcePageCategory: props.contentGroup }}
                >
                  <NewSrp
                    adInfo={adInfo}
                    vehicles={vehicles}
                    isLoading={isLoading}
                    totalCount={totalCount}
                    searchVehicles={onSearch}
                    searchUrlProps={props.searchUrlAttributes}
                    vehicleOverview={props.vehicleOverview}
                    ownerReviewsProps={props.ownerReviews}
                    make={initialParams.searchUrlAttributes?.make_name}
                    model={initialParams.searchUrlAttributes?.model_name}
                    dealerReviewsProps={props.dealerReviews}
                    articlePreviews={props.articlePreviews}
                    drivingTipsAndExperiences={props.drivingTipsAndExperiences}
                    contentGroup={props.contentGroup}
                    topDealers={props.topDealers}
                    facetCounts={facetCounts}
                  />
                </SrpEventContext.Provider>
              </>
            )}
          </Layout>
        </AdGlobalRefreshProvider>
      </div>
      <StructuredDataComponent data={props.searchUrlAttributes?.breadcrumb_structured} />
      {initialParams.searchParams &&
        props.ownerReviews?.ownerReviewSummary.reviewCount &&
        (props.searchUrlAttributes?.type === 'MakeModelYearSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelLocationSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelSearchUrl') && (
          <StructuredDataComponent
            data={{
              '@type': 'Product',
              '@id': 'https://www.autolist.com/landing-page-srp-view',
              name: initialParams.searchParams.year
                ? `${initialParams.searchParams.year} ${initialParams.searchParams.make} ${initialParams.searchParams.model}`
                : `${initialParams.searchParams.make} ${initialParams.searchParams.model}`,
              mpn: initialParams.searchParams.year
                ? `${initialParams.searchParams.year} ${initialParams.searchParams.make} ${initialParams.searchParams.model}`
                : `${initialParams.searchParams.make} ${initialParams.searchParams.model}`,
              description: `${
                initialParams.searchParams.year
                  ? `${initialParams.searchParams.year} ${initialParams.searchParams.make} ${initialParams.searchParams.model}`
                  : `${initialParams.searchParams.make} ${initialParams.searchParams.model}`
              } for sale`,
              ...(props.vehicles && {
                image: props.vehicles[0]?.images[0],
                sku: props.vehicles[0]?.id,
              }),
              brand: initialParams.searchParams.make,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: props.ownerReviews?.ownerReviewSummary?.overallRating,
                reviewCount: props.ownerReviews?.ownerReviewSummary?.reviewCount,
              },
            }}
          />
        )}
      {props.ownerReviews &&
        props.ownerReviews?.ownerReviewSummary.reviewCount &&
        (props.searchUrlAttributes?.type === 'MakeModelYearSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelLocationSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelSearchUrl') && (
          <StructuredDataComponent
            data={{
              '@id': 'https://www.autolist.com/landing-page-srp-view',
              review: props.ownerReviews.ownerReviews.map((review) => ({
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  // eslint-disable-next-line no-nested-ternary
                  name: review.reviewerName
                    ? review.reviewerName.replace('"', '"')
                    : props.searchUrlAttributes?.model_name
                    ? `${props.searchUrlAttributes.model_name} Owner`
                    : 'Anonymous',
                },
                name: review.title?.replace('"', '"'),
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: review.overallRating,
                },
              })),
            }}
          />
        )}
      {totalCount &&
        totalCount > 0 &&
        props.ownerReviews?.ownerReviewSummary.reviewCount &&
        (props.searchUrlAttributes?.type === 'MakeModelYearSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelLocationSearchUrl' ||
          props.searchUrlAttributes?.type === 'MakeModelSearchUrl') && (
          <StructuredDataComponent
            data={{
              '@context': 'https://schema.org',
              '@id': 'https://www.autolist.com/landing-page-srp-view',
              offers: {
                '@type': 'AggregateOffer',
                lowPrice,
                highPrice,
                priceCurrency: 'USD',
                offerCount: totalCount,
              },
            }}
          />
        )}
    </div>
  );
}

function telemetry(req: IncomingMessage, res: ServerResponse): string {
  const start = Date.now();
  const requestId = uuidv4();

  res.on('finish', () => {
    const end = Date.now();
    const duration = end - start;
    console.log(
      `[${new Date(start).toISOString()}] ${requestId} - ${req.url} took ${duration}ms to load.`
    );
  });

  return requestId;
}

export type SeoSrpPageParams = {
  parentPage: string;
};

export const getServerSideProps: GetServerSideProps<SeoSrpPageProps, SeoSrpPageParams> =
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  wrapper.getServerSideProps((store) => async (ctx) => {
    const start = Date.now();
    let serverTiming = '';
    ctx.res.setHeader('Cache-Control', 'public, max-age=86400, stale-while-revalidate');
    const requestId = await telemetry(ctx.req, ctx.res);
    try {
      const term = ctx.params?.parentPage as string;
      const locale = ctx.locale ?? 'en';
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const [searchUrl, fsuDuration] = await withTiming(fetchSearchUrl, requestId)(term!, {
        locale,
      });
      if (!searchUrl) {
        ctx.res.statusCode = 404;
        return {
          notFound: true,
          props: {
            statusCode: 404,
          },
        };
      }
      serverTiming += `fsu;desc="fsu";dur=${fsuDuration},`;

      const {
        data: {
          attributes: {
            search_params: searchParams,
            location_display_name: locationDisplayName,
            type,
            location_id: locationId,
            redirect_url: redirectUrl,
          },
        },
      } = searchUrl;

      // Redirect if Search URL returns a redirect_url, indicates the original page has been deprecated
      if (redirectUrl) {
        return {
          redirect: {
            destination: `/${redirectUrl}`,
            permanent: true,
          },
          props: {
            statusCode: 301,
          },
        };
      }
      let latitude;
      let longitude;
      let city;
      let stateCode;
      const isLocationUrl = searchParams?.latitude && searchParams?.longitude;
      const metroCode = ctx.req.headers['cloudfront-viewer-metro-code'] as string;
      const hasMetroCode = !!metroCode && (MetroCodes as MetroCodesType)[metroCode];
      if (isLocationUrl) {
        const { latitude: searchParamsLatitude, longitude: searchParamsLongitude } = searchParams;
        latitude = searchParamsLatitude;
        longitude = searchParamsLongitude;
        if (locationDisplayName) {
          [city, stateCode] = locationDisplayName.split(', ');
        }
      }
      if (!isLocationUrl) {
        if (hasMetroCode) {
          const { latitude: metroLatitude, longitude: metroLongitude } = (
            MetroCodes as MetroCodesType
          )[metroCode];
          latitude = metroLatitude;
          longitude = metroLongitude;
        } else {
          let redisCachedMaxMindResult;
          const ip = await getIpAddress(ctx.req);

          if (ip) {
            redisCachedMaxMindResult = await getIpMaxMindValue(ip);
            if (redisCachedMaxMindResult) {
              /* eslint-disable prefer-destructuring */
              latitude = redisCachedMaxMindResult.latitude;
              longitude = redisCachedMaxMindResult.longitude;
            } else if (!redisCachedMaxMindResult) {
              const [maxmindResult, grDuration] = await withTiming(
                geolocateRequest,
                requestId
              )(ctx.req);
              serverTiming += `gr;desc="gr";dur=${grDuration},`;
              if (maxmindResult) {
                const { latitude: maxmindLatitude, longitude: maxmindLongitude } = maxmindResult;
                latitude = maxmindLatitude;
                longitude = maxmindLongitude;

                await setIpMaxMindValue({ ip, value: maxmindResult });
              }
            }
          }
        }

        if (!latitude && !longitude) {
          latitude = 26.2445263;
          longitude = -80.206436;
        }
      }

      const isLocationSearchUrl = type?.includes('Location');
      const locationSearchParams = isLocationSearchUrl
        ? {
            sort_filter: type?.includes('Location') ? 'distance:asc' : undefined,
            radius: type?.includes('Location') ? 50 : searchParams.radius,
          }
        : {
            city,
            state: stateCode,
          };
      if (searchParams.condition && !Array.isArray(searchParams.condition)) {
        searchParams.condition = [searchParams.condition];
      }
      const searchVehiclesParams: SearchParamsV2 = {
        ...searchParams,
        latitude,
        longitude,
        ...locationSearchParams,
        sort_new_cars_last: true,
        locale: i18next.language,
        sort_filter: 'blended_score:desc',
      };

      const apiFilterValues = transformSrpFilterApiParamsToState(
        searchVehiclesParams as SearchFilterUrlParams
      );
      store.dispatch(setSrpFilters(apiFilterValues));
      if (!searchVehiclesParams.radius) {
        searchVehiclesParams.radius = srpFilterInitialValues[SrpFilterName.Radius];
      }
      let [results, svDuration] = await withTiming(searchVehicles, requestId)(searchVehiclesParams);
      serverTiming += `sv1;desc="sv1";dur=${svDuration},`;
      if (results.totalCount < 10 && !isLocationSearchUrl) {
        const paramsWithRadius = {
          ...searchVehiclesParams,
          radius: 'Any',
        };
        [results, svDuration] = await withTiming(searchVehicles, requestId)(paramsWithRadius);
        serverTiming += `sv2;desc="sv2";dur=${svDuration},`;
        store.dispatch(setSrpFilters({ [SrpFilterName.Radius]: 'Any' }));
      }

      const [ownerReviews, fmrDuration] = await withTiming(fetchModelReviews, requestId)(
        term!,
        type!
      );
      serverTiming += `fmr;desc="fmr";dur=${fmrDuration},`;

      const [vehicleOverview, fvoDuration] = await withTiming(
        fetchVehicleOverview,
        requestId
      )(term!);
      serverTiming += `fvo;desc="fvo";dur=${fvoDuration},`;

      const [dealerReviews, fdrDuration] = await withTiming(fetchDealerReviews, requestId)(term!);
      serverTiming += `fdr;desc="fdr";dur=${fdrDuration},`;

      const [articlePreviews, fapoppDuration] = await withTiming(
        fetchArticlePreviewsOfParentPage,
        requestId
      )(term!);
      serverTiming += `fapopp;desc="fapopp";dur=${fapoppDuration},`;

      const [drivingTipsAndExperiences, fdeocDuration] = locationId
        ? await withTiming(fetchDrivingExperiencesOfCity, requestId)(locationId)
        : [null, undefined];
      if (fdeocDuration) serverTiming += `fdeoc;desc="fdeoc";dur=${fdeocDuration},`;

      let topDealers = null;
      let ftdDuration;
      if (locationDisplayName && latitude && longitude) {
        [topDealers, ftdDuration] = await withTiming(fetchTopDealers, requestId)(
          latitude,
          longitude
        );
      }
      if (ftdDuration) serverTiming += `ftd;desc="ftd";dur=${ftdDuration},`;

      serverTiming += `gssp;desc="gssp";dur=${Date.now() - start}`;
      ctx.res.setHeader('Server-Timing', serverTiming);
      // Before returning props in getServerSideProps
      const normalizedAttributes = {
        ...searchUrl.data.attributes,
        search_params: {
          ...searchParams,
          sort_new_cars_last: true,
          locale: i18next.language,
          sort_filter: searchVehiclesParams.sort_filter,
        },
      };
      // Check if type doesn't contain both 'Make' and 'Model'
      // Conditionally create a banner title containing year/make/model/trim for AdvancedHeader
      if (type) {
        const hasYear = type.includes('Year');
        const hasMake = type.includes('Make');
        const hasModel = type.includes('Model');
        const hasTrim = type.includes('Trim');

        normalizedAttributes.make_name = hasMake ? normalizedAttributes.make_name : null;
        normalizedAttributes.model_name = hasModel ? normalizedAttributes.model_name : null;

        const titleSections = [];

        if (hasYear) {
          titleSections.push(normalizedAttributes.year);
        }
        if (hasMake) {
          titleSections.push(normalizedAttributes.make_name);
        }
        if (hasModel) {
          titleSections.push(normalizedAttributes.model_name);
        }
        if (hasTrim) {
          titleSections.push(normalizedAttributes.search_params.trim.join(' '));
        }
        normalizedAttributes.bannerTitle = titleSections.join(' ');
      }

      return {
        props: {
          statusCode: 200,
          vehicles: results.vehicles,
          totalCount: results.totalCount,
          searchUrlAttributes: normalizedAttributes,
          linkSet: searchUrl.data.attributes.link_set,
          breadcrumbData: searchUrl.data.attributes.breadcrumb_structured,
          latitude: searchVehiclesParams.latitude,
          longitude: searchVehiclesParams.longitude,
          vehicleOverview: vehicleOverview?.vehicleOverviews?.[0] ?? null,
          dealerReviews: dealerReviews || null,
          ownerReviews: ownerReviews || null,
          articlePreviews: articlePreviews || null,
          drivingTipsAndExperiences: drivingTipsAndExperiences || null,
          contentGroup: type || '',
          topDealers,
          facetCounts: results.facetCounts,
        },
      };
    } catch (error) {
      captureException(error);
      ctx.res.statusCode = 500;
      return {
        props: {
          statusCode: 500,
        },
      };
    }
  });
